import { Badge, Table, Tooltip } from '@iveco-ti/iveco-ds'
import {
  KeyRound,
  Link,
  LogOut,
  ShieldUser,
  Unlink,
  UnlockKeyhole,
  UserCheck,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useAppAuthenticationManager } from '@/common/components/AppAuthenticationManager'
import { useAppConnectionManager } from '@/common/components/AppConnectionManager'
import { useForgotApplicationPassword } from '@/common/hooks/useForgotApplicationPassword'
import { ApplicationAccountUserStatus } from '@/common/models/applicationAccountUser'
import { translateApplicationAccountUserRole } from '@/common/utils/translateApplicationAccountUserRole'
import { useAccessDisconnectManager } from '../../../components/AccessDisconnectManager'
import { useAccessPasswordChangeManager } from '../../../components/AccessPasswordChangeManager'
import { useAccessRemovalManager } from '../../../components/AccessRemovalManager'
import { useChangeIngressAccount } from '../hooks/useChangeIngressAccount'
import { CurrentUserAccounts } from '../hooks/useGetCurrentUserAccounts'

type ProfileAccessTableRowProps = {
  accountUser: CurrentUserAccounts['ApplicationAccountUsers'][0]
  applicationTitle: string
  allConnectedAccounts: CurrentUserAccounts['ApplicationAccountUsers']
}

export function ProfileAccessTableRow({
  accountUser,
  applicationTitle,
  allConnectedAccounts,
}: ProfileAccessTableRowProps) {
  const { t } = useTranslation(['profile', 'common'])
  const { handleRemoveApplicationAccess, handleIngressAccountWarning } =
    useAccessRemovalManager()
  const { handleChangeApplicationPassword } = useAccessPasswordChangeManager()
  const { handleDisconnectApplication } = useAccessDisconnectManager()
  const { handleAuthenticateApplication } = useAppAuthenticationManager()
  const { handleConnectApplication } = useAppConnectionManager()
  const { handleChangeIngressAccount } = useChangeIngressAccount()
  const { handleForgotApplicationPassword } = useForgotApplicationPassword()

  const hasConnectedIngressAccount = allConnectedAccounts.some(
    (account) =>
      account.isIngress &&
      account.status === ApplicationAccountUserStatus.CONNECTED,
  )

  const handleChangePassword = () => {
    handleChangeApplicationPassword(
      accountUser.id,
      applicationTitle,
      accountUser.Account?.title || t('access.account_label'),
    )
  }

  const handleConnect = () => {
    if (!accountUser.Application?.id) return

    handleConnectApplication(
      accountUser.Application.id,
      accountUser.userId,
      applicationTitle,
      accountUser.Account?.title,
    )
  }

  const handleAuthenticate = () => {
    if (!accountUser.Application?.id) return

    handleAuthenticateApplication(
      accountUser.Application.id,
      accountUser.userId,
      applicationTitle,
      accountUser.id,
      accountUser.Account?.title,
    )
  }

  const handleDisconnect = () => {
    const authId = accountUser.ApplicationAccountUsersAuth?.[0]?.id
    if (!authId) return

    handleDisconnectApplication(
      authId,
      applicationTitle,
      accountUser.Account?.title || t('access.account_label'),
    )
  }

  const handleMakeIngressAccount = () => {
    handleChangeIngressAccount(
      accountUser.id,
      applicationTitle,
      accountUser.Account?.title || t('access.account_label'),
    )
  }

  const handleRemoveAccess = () => {
    const otherConnectedAccounts = allConnectedAccounts.filter(
      (user) =>
        user.id !== accountUser.id &&
        user.status === ApplicationAccountUserStatus.CONNECTED,
    )

    const isCurrentAccountIngress = accountUser.isIngress
    const shouldShowWarning =
      isCurrentAccountIngress && otherConnectedAccounts.length > 0

    if (shouldShowWarning) {
      const accountNames = otherConnectedAccounts.map(
        (user) => user.Account?.title || t('access.account_label'),
      )

      handleIngressAccountWarning(
        applicationTitle,
        accountUser.Account?.title || '',
        accountNames,
      )
    } else {
      handleRemoveApplicationAccess(
        accountUser.id,
        applicationTitle,
        accountUser.Account?.title || t('access.account_label'),
        accountUser.isIngress,
      )
    }
  }

  const handleForgotPassword = () => {
    handleForgotApplicationPassword(accountUser.id)
  }

  const getStatusBadge = (status: ApplicationAccountUserStatus) => {
    switch (status) {
      case ApplicationAccountUserStatus.CONNECT:
        return (
          <Badge.Root colors="base" size="sm">
            {t('access.status.connect')}
          </Badge.Root>
        )
      case ApplicationAccountUserStatus.AUTHENTICATE:
        return (
          <Badge.Root colors="warning" size="sm">
            {t('access.status.authenticate')}
          </Badge.Root>
        )
      case ApplicationAccountUserStatus.CONNECTED:
        return (
          <Badge.Root colors="success" size="sm">
            {t('access.status.connected')}
          </Badge.Root>
        )
      default:
        return null
    }
  }

  const getActionButtons = () => {
    const hasAuth =
      accountUser.ApplicationAccountUsersAuth &&
      accountUser.ApplicationAccountUsersAuth.length > 0
    const shouldShowAuthenticateActions =
      accountUser.status === ApplicationAccountUserStatus.AUTHENTICATE ||
      (accountUser.status === ApplicationAccountUserStatus.CONNECTED &&
        !hasAuth &&
        accountUser.isIngress)

    const buttons = {
      forgotPassword: shouldShowAuthenticateActions &&
        accountUser.isIngress && (
          <Tooltip
            key="forgot-password"
            text={t('access.actions.forgot_password')}
          >
            <button
              type="button"
              className="hover:bg-base-100 p-1 rounded"
              onClick={handleForgotPassword}
            >
              <UnlockKeyhole size={18} className="text-brand-900" />
            </button>
          </Tooltip>
        ),

      changePassword: accountUser.isIngress && (
        <Tooltip
          key="change-password"
          text={t('access.actions.change_password')}
        >
          <button
            type="button"
            className="hover:bg-base-100 p-1 rounded"
            onClick={handleChangePassword}
          >
            <KeyRound size={18} className="text-brand-900" />
          </button>
        </Tooltip>
      ),

      authenticate: shouldShowAuthenticateActions && (
        <Tooltip key="authenticate" text={t('access.actions.authenticate')}>
          <button
            type="button"
            className="hover:bg-base-100 p-1 rounded"
            onClick={handleAuthenticate}
          >
            <ShieldUser size={18} className="text-success-900" />
          </button>
        </Tooltip>
      ),

      disconnect: accountUser.isIngress && (
        <Tooltip key="disconnect" text={t('access.actions.disconnect')}>
          <button
            type="button"
            className="hover:bg-base-100 p-1 rounded"
            onClick={handleDisconnect}
          >
            <Unlink size={18} className="text-warning-900" />
          </button>
        </Tooltip>
      ),

      makeIngress: !accountUser.isIngress && hasConnectedIngressAccount && (
        <Tooltip key="make-ingress" text={t('access.actions.make_ingress')}>
          <button
            type="button"
            className="hover:bg-base-100 p-1 rounded"
            onClick={handleMakeIngressAccount}
          >
            <UserCheck size={18} className="text-brand-900" />
          </button>
        </Tooltip>
      ),

      connect: accountUser.status === ApplicationAccountUserStatus.CONNECT &&
        accountUser.isIngress && (
          <Tooltip key="connect" text={t('access.actions.connect')}>
            <button
              type="button"
              className="hover:bg-base-100 p-1 rounded"
              onClick={handleConnect}
            >
              <Link size={18} className="text-success-900" />
            </button>
          </Tooltip>
        ),

      removeAccess: (
        <Tooltip key="remove-access" text={t('access.actions.remove_access')}>
          <button
            type="button"
            className="hover:bg-base-100 p-1 rounded"
            onClick={handleRemoveAccess}
          >
            <LogOut size={18} className="text-danger-900" />
          </button>
        </Tooltip>
      ),
    }

    if (shouldShowAuthenticateActions) {
      return [
        buttons.forgotPassword,
        buttons.authenticate,
        buttons.removeAccess,
      ].filter(Boolean)
    }

    return [
      buttons.changePassword,
      buttons.disconnect,
      buttons.makeIngress,
      buttons.connect,
      buttons.removeAccess,
    ].filter(Boolean)
  }

  return (
    <Table.Row hideCheckbox id={accountUser.id} key={accountUser.id}>
      <Table.Data>
        <div className="flex flex-col gap-2">
          <span className="font-medium">
            {accountUser.Account?.title || t('access.account_label')}
          </span>
          <div className="flex items-center gap-2">
            {getStatusBadge(accountUser.status)}
            {accountUser.isIngress && (
              <Badge.Root colors="brand" size="sm">
                {t('access.ingress_account')}
              </Badge.Root>
            )}
          </div>
        </div>
      </Table.Data>

      <Table.Data>
        <Badge.Root>
          {translateApplicationAccountUserRole(t, accountUser.role)}
        </Badge.Root>
      </Table.Data>

      <Table.Data end>
        <div className="w-[200px] flex gap-2 justify-end">
          {getActionButtons()}
        </div>
      </Table.Data>
    </Table.Row>
  )
}
